import { useEffect, useState } from "react";
import { toast } from "../components/ui/sonner";
import { FloppyDisk, CreditCard } from "@phosphor-icons/react";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getSettings, updateSettings, getPipelineStatus, createPortalSession, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    getSettings().then(setSettings).catch(() => setSettings(null));
    getPipelineStatus()
      .then((d) => setStages(d.stages || []))
      .catch(() => setStages([]));
  }, []);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    getSettings().then(setSettings).catch(() => setSettings(null));
    getPipelineStatus()
      .then((d) => setStages(d.stages || []))
      .catch(() => setStages([]));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        preferred_leagues: settings.preferred_leagues,
        odds_format: settings.odds_format,
        timezone: settings.timezone,
      });
      setSettings(updated);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="mx-auto max-w-[1000px] px-4 py-10 text-zinc-500">Loading settings&hellip;</div>;
  }

  return (
    <div data-testid="settings-page" className="mx-auto max-w-[1000px] px-4 py-8 sm:px-6">
      <h1 className="mb-6 font-heading text-3xl uppercase tracking-tight text-white">Settings</h1>

      <div data-testid="account-section" className="mb-6 rounded-lg border border-white/10 bg-eb-surface p-5">
        <h2 className="mb-4 font-heading text-lg uppercase tracking-tight text-white">Account</h2>
        <div className="mb-4 flex flex-col gap-1 text-sm text-zinc-300 sm:flex-row sm:items-center sm:justify-between">
          <span data-testid="account-email">{user?.email}</span>
          <span data-testid="account-subscription-status" className="font-mono text-xs uppercase text-zinc-500">
            Subscription: {user?.subscription_status || "unknown"}
          </span>
        </div>
        {user?.bb_linked && (
          <p data-testid="bb-linked-indicator" className="mb-4 font-mono text-xs uppercase tracking-wide text-emerald-400">
            Bankroll Builder Premium is linked to this account
          </p>
        )}
        <button
          data-testid="manage-subscription-button"
          onClick={handleManageSubscription}
          disabled={portalLoading}
          className="flex items-center gap-2 rounded-md border border-eb-accent/40 bg-eb-accent/10 px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:border-eb-accent disabled:opacity-50"
        >
          <CreditCard size={16} weight="bold" /> {portalLoading ? "Opening\u2026" : "Manage Subscription"}
        </button>
      </div>

      <div className="mb-6 rounded-lg border border-white/10 bg-eb-surface p-5">
        <h2 className="mb-4 font-heading text-lg uppercase tracking-tight text-white">Preferences</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="mb-2 block text-xs uppercase text-zinc-500">Odds Format</Label>
            <Select value={settings.odds_format} onValueChange={(v) => setSettings({ ...settings, odds_format: v })}>
              <SelectTrigger data-testid="settings-odds-format-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="american">American</SelectItem>
                <SelectItem value="decimal">Decimal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-2 block text-xs uppercase text-zinc-500">Timezone</Label>
            <Select value={settings.timezone} onValueChange={(v) => setSettings({ ...settings, timezone: v })}>
              <SelectTrigger data-testid="settings-timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">America/New York</SelectItem>
                <SelectItem value="America/Mexico_City">America/Mexico City</SelectItem>
                <SelectItem value="Asia/Tokyo">Asia/Tokyo</SelectItem>
                <SelectItem value="Asia/Taipei">Asia/Taipei</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-white/10 bg-eb-surface p-5">
        <h2 className="mb-4 font-heading text-lg uppercase tracking-tight text-white">Data Pipeline Status</h2>
        <div className="flex flex-col gap-2">
          {stages.map((stage) => (
            <div key={stage.key} className="flex items-center justify-between rounded-md border border-white/5 px-3 py-2">
              <span className="text-sm text-zinc-300">{stage.label}</span>
              <StatusBadge status={stage.status} testId={`settings-pipeline-status-${stage.key}`} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-zinc-600">
          Free data sources and the prediction methodology will be connected in a later phase.
        </p>
      </div>

      <button
        data-testid="settings-save-button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 rounded-md bg-eb-accent px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
      >
        <FloppyDisk size={16} weight="bold" /> {saving ? "Saving\u2026" : "Save Settings"}
      </button>
    </div>
  );
};

export default Settings;
